/** Shared pricing translation shape — imported by all locale files. */

import {
  WC2026_ORG_BILLING_DISCLOSURE,
  WC2026_ORG_BILLING_DISCLOSURE_SHORT,
} from '../../shared/constants/wc2026OrgBillingDisclosure.constants'

export const pricingTranslationsEn = {
  eyebrow: 'Packages & pricing',
  perMonth: 'per month',
  perYear: 'per year',
  copy: {
    pageTitle: 'Plans & Pricing',
    pageSubtitle:
      'One place to compare plans. Hobby is free for everyone. Pro is a personal subscription. Business is a one-time WC 2026 org fee (not Pro). Enterprise is negotiated with our team.',
    comingSoonBadge: 'Coming soon',
    comingSoonInProduction: 'Coming soon in production',
    mostPopularBadge: 'Most popular',
    currentPlanBadge: 'Your plan',
    createOrgCta: 'Create organization',
    footnote:
      'Pro is billed to your personal account (no org). Business is a one-time WC 2026 organization fee — create an org, pick your seat tier, and pay once for tournament-period org access. Enterprise is for organizations that need custom pricing, limits, or contracts — contact us.',
    orgPlanDisclosure: WC2026_ORG_BILLING_DISCLOSURE,
    orgPlanDisclosureShort: WC2026_ORG_BILLING_DISCLOSURE_SHORT,
    footnoteCancelPolicy:
      'If you cancel Pro in the billing portal, Stripe keeps your plan active until the end of the paid period — you can resume before then. The app syncs with Stripe when you return here.',
    signInToSubscribe: 'Sign in to subscribe',
    logIn: 'Log in',
    goBack: 'Back',
    manageSubscription: 'Manage subscription',
    proAccessEndsOn: 'Pro access ends',
    billingIntervalMonth: 'Monthly',
    billingIntervalYear: 'Yearly',
    switchToMonthly: 'Switch to monthly',
    upgradeToYearlyDialogMsg:
      "Yearly billing is a 12-month commitment. Once switched, you won't be able to revert to monthly billing until your yearly period ends — no partial refunds.",
    upgradeToYearlyDialogConfirm: 'Switch to yearly',
    upgradeToYearlyDialogCancel: 'Keep monthly',
    switchToMonthlyDialogTitle: 'Switch to monthly Pro',
    switchToMonthlyDialogMsg:
      "You'll be taken to Stripe to switch to monthly billing. Your yearly Pro access continues until the current period ends — monthly billing starts after that. No refund for unused time.",
    switchToMonthlyConfirm: 'Go to Stripe',
    switchToMonthlyCancel: 'Keep yearly',
    upgradeToYearlyProTitle: 'Upgrade to Yearly Pro',
    upgradeToYearlyProConfirm: 'Upgrade',
    enterpriseSubscribe: 'Subscribe',
    enterpriseCheckoutAdminOnly: 'Ask an org admin to subscribe',
    upgradeSeatBucket: 'Upgrade',
    managePlan: 'Plan & billing',
    businessSubscribe: 'Subscribe',
    upgradeMonthlyToYearlyDialog: {
      line1: "You're switching from Monthly Pro ({monthly}/month) to Yearly Pro ({yearly}/year).",
      line2:
        'Your existing payment method will be charged the prorated difference for the current billing period.',
      line3:
        "Once on yearly, you won't be able to revert to monthly billing until the yearly period ends.",
      line4: 'Continue?',
    },
  },
  plans: {
    hobby: {
      name: 'Hobby',
      priceLabel: 'Free',
      tagline: 'For friends and small groups getting started.',
      ctaLabel: 'Get started',
      features: [
        'Core challenges and predictions',
        'Standard scoring (no doubles)',
        'Standard match rows per challenge',
        'Standard support',
        'Occasional ads',
        'Great for trying Zporti with a few people',
      ],
    },
    proMonthly: {
      name: 'Pro · Monthly',
      tagline: 'For solo players and friend groups who want more features.',
      ctaLabel: 'Subscribe monthly',
      features: [
        'Everything in Hobby',
        'Doubles enabled',
        'Higher match rows per challenge',
        'Ad-free experience',
        'Higher limits for members and activity',
        'Priority support',
        'Stronger branding options',
      ],
    },
    proYearly: {
      name: 'Pro · Yearly',
      tagline: 'Same Pro features, billed once per year — best when your group is in for the long run.',
      ctaLabel: 'Subscribe yearly',
      features: [
        'Everything in Hobby',
        'Doubles enabled',
        'Higher match rows per challenge',
        'Ad-free experience',
        'Higher limits for members and activity',
        'Priority support',
        'Stronger branding options',
      ],
    },
    business: {
      name: 'Business',
      priceLabel: 'From $450',
      tagline: 'One-time WC 2026 org fee. Pick your seat tier and pay once for tournament access.',
      ctaLabel: 'Get started',
      features: [
        'Organization branding (colors & logos)',
        'Member seats — flat-rate tiers up to 2,000',
        'WC 2026 tournament org access (branded org + linked squad)',
        'Priority support',
        'Does not include personal Pro, coins, boosts, or in-app store purchases',
      ],
    },
    enterprise: {
      name: 'Enterprise',
      priceLabel: 'Custom',
      tagline: 'Negotiated org pricing and rollout for WC 2026 — scoped in your order or contract.',
      ctaLabel: 'Contact us',
      features: [
        'Organization branding and rollout (by agreement)',
        'Member limits scoped with sales',
        'WC 2026 and org-wide features (by agreement)',
        'Dedicated support & custom onboarding',
        'Custom integrations, invoicing & contracts',
        'Does not include personal Pro, coins, boosts, or in-app store purchases',
      ],
    },
  },
  seatBuckets: {
    upTo100: 'Up to 100 employees',
    upTo250: 'Up to 250 employees',
    upTo500: 'Up to 500 employees',
    upTo1000: 'Up to 1,000 employees',
    upTo2000: 'Up to 2,000 employees',
  },
} as const

export const featuresSectionEn = {
  eyebrow: 'What you get',
  livePreview: 'Live preview',
  avatars: {
    title: 'Customized avatars for every player',
    description:
      'Give each person a recognizable identity so standings, rivalries, and trash-talk feel personal from day one.',
    accessibilityLabel: 'Customized avatars feature preview',
  },
} as const

export const footerExtendedEn = {
  product: 'Product',
  compete: 'Compete',
  company: 'Company',
  phoneTagline: 'Sports predictions with friends.',
  copyright: '© 2026 Zporti. Made by football fans, for football fans.',
  copyrightShort: '© 2026 Zporti',
} as const

export const organizationsEn = {
  heroBadge: 'For teams, leagues & brands',
  heroTitle: 'Organizations on Zporti',
  heroLead:
    'Run your group with its own identity inside Zporti. Set brand colors and logos, invite members with a join code, and manage seats from one place—so everyone sees a cohesive experience when they play and predict together.',
  valueSectionTitle: 'What you get',
  valueBrandingTitle: 'Brand that feels yours',
  valueBrandingBody:
    'Upload your logos, pick primary and secondary colors, and preview how they show up across the experience. Admins keep the look consistent without touching code.',
  valueMembersTitle: 'Members & seats',
  valueMembersBody:
    'Create an organization or join with a code. See who is in, manage capacity with seat limits on your plan, and copy a join code when you onboard new people.',
  valueMascotTitle: 'Branded mascot & avatars',
  valueMascotBody:
    'Optional branded mascot moods and organization-only avatar sets help your community stand out—great for fan groups, company leagues, and partners.',
  howSectionTitle: 'How it works',
  howStep1: 'Sign in, then create an organization or join with an invite code from your admin.',
  howStep2: 'Admins open Organization settings to adjust branding, members, and account details.',
  howStep3: 'Members use the app as usual, with your organization’s visuals and benefits where enabled.',
  signIn: 'Sign in',
  signUp: 'Create account',
  heroPanelTitle: 'Organization control room',
  heroPanelBody:
    'Invite members, set branding, and give every group a polished home before kickoff.',
} as const

export const b2bContactEn = {
  title: 'Prefer we set it up for you?',
  subtitle: 'Fill in your details and our team will reach out.',
  orgNameLabel: 'Organization name *',
  orgNamePlaceholder: 'Your organization',
  contactNameLabel: 'Your name *',
  contactNamePlaceholder: 'Full name',
  emailLabel: 'Email *',
  emailPlaceholder: 'you@example.com',
  messageLabel: 'Message',
  messagePlaceholder: 'Tell us about your needs (optional)',
  submitButton: 'Send Request',
  scheduleMemo: 'Or schedule a call — coming soon',
  errorFallbackValidation: 'Please check your input and try again.',
  errorFallbackSubmit: 'Submission failed. Please try again.',
  successMessage: 'Thank you! We received your request and will be in touch shortly.',
} as const

export const signInEn = {
  signUpSubtitle: 'Sign up to get started',
  signInSubtitle: 'Sign in to continue',
  emailPlaceholder: 'Email',
  passwordPlaceholder: 'Password',
  forgotPassword: 'Forgot password?',
  signUpButton: 'Sign Up',
  signInButton: 'Sign In',
  alreadyHaveAccount: 'Already have an account? ',
  noAccount: "Don't have an account? ",
  toggleSignIn: 'Sign in',
  toggleSignUp: 'Sign up',
  orDivider: 'OR',
  signInWith: 'Sign in with',
  continueAsGuest: 'or continue as guest',
  legalConsentPrefix: 'I have read and agree to the ',
  legalConsentAnd: ' and ',
  legalConsentSuffix: '.',
  marketingConsent: 'I agree to receive product updates and marketing emails from Zporti.',
  loading: 'Loading…',
  sendingResetEmail: 'Sending reset email',
  signingUp: 'Signing up',
  signingIn: 'Signing in',
  continuingWithGoogle: 'Continuing with Google',
  continuingWithFacebook: 'Continuing with Facebook',
  enteringAsGuest: 'Entering as guest',
} as const

export const a11yEn = {
  openMenu: 'Open menu',
  closeMenu: 'Close menu',
  heroMascot: 'Zporti mascot holding a phone',
} as const

export const accountAdminSiteEn = {
  backToSite: '← Back to site',
} as const
